import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiRequest, clearSession, setSessionExpiredHandler } from "../lib/apiClient";
import { signInWithCustomToken, signOut } from "firebase/auth";
import { auth } from "../firebase/firebaseWeb";

const defaultContext = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  error: "",
  login: async () => {},
  registerRenter: async () => {},
  registerPersonal: async () => {},
  registerBusiness: async () => {},
  logout: async () => {},
  clearError: () => {},
};

export const AuthContextWeb = createContext(defaultContext);

const normalizeRole = (role) =>
  typeof role === "string" ? role.trim().toUpperCase() : "";

const parseStoredUser = () => {
  const raw = localStorage.getItem("rh_user");
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export function AuthProviderWeb({ children }) {
  const [user, setUser] = useState(parseStoredUser);
  const [token, setToken] = useState(localStorage.getItem("rh_access_token"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const clearError = useCallback(() => setError(""), []);

  // A request's token refresh can fail on any page, at any time (e.g. a
  // stale token from a previous session). When that happens, apiClient
  // clears localStorage but has no way to clear this component's React
  // state itself — so it calls this handler to keep them in sync, which
  // in turn makes ProtectedRoute redirect to login instead of a page
  // showing the raw "Invalid token" error underneath it.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      setUser(null);
      setToken(null);
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  const login = useCallback(async ({ email, password }) => {
    setLoading(true);
    setError("");

    try {
      const payload = await apiRequest("/auth/login", {
        method: "POST",
        auth: false,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const authToken = payload?.token || payload?.accessToken || payload?.jwt;
      const firebaseToken =
        payload?.firebaseToken || payload?.firebaseCustomToken;
      const role = normalizeRole(payload?.role);

      if (!authToken) {
        throw new Error("Login response did not include an access token.");
      }

      if (!role) {
        throw new Error("Login response did not include a valid role.");
      }

      const nextUser = {
        userId: payload.userId,
        name: payload.name,
        email: payload.email,
        role,
      };

      localStorage.setItem("rh_access_token", authToken);
      if (payload.refreshToken) {
        localStorage.setItem("rh_refresh_token", payload.refreshToken);
      }
      localStorage.setItem("rh_user", JSON.stringify(nextUser));

      // Match mobile behavior: sign into Firebase directly using login response.
      if (firebaseToken) {
        try {
          await signInWithCustomToken(auth, firebaseToken);
        } catch {
          // Keep backend login valid even if Firebase signin transiently fails.
        }
      }

      setUser(nextUser);
      setToken(authToken);

      return nextUser;
    } catch (err) {
      const message = err?.message || "Unable to login.";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const registerRenter = useCallback(async (formValues) => {
    setLoading(true);
    setError("");

    try {
      const formData = new FormData();
      Object.entries(formValues).forEach(([key, value]) => {
        formData.append(key, value);
      });

      return await apiRequest("/auth/register-renter", {
        method: "POST",
        auth: false,
        body: formData,
      });
    } catch (err) {
      const message = err?.message || "Unable to register.";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const registerPersonal = useCallback(async (formValues) => {
    setLoading(true);
    setError("");
    try {
      const formData = new FormData();
      Object.entries(formValues).forEach(([key, value]) => {
        formData.append(key, value);
      });
      return await apiRequest("/auth/register-personal", {
        method: "POST",
        auth: false,
        body: formData,
      });
    } catch (err) {
      const message = err?.message || "Unable to register.";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const registerBusiness = useCallback(async (formValues) => {
    setLoading(true);
    setError("");
    try {
      const formData = new FormData();
      Object.entries(formValues).forEach(([key, value]) => {
        formData.append(key, value);
      });
      return await apiRequest("/auth/register-business", {
        method: "POST",
        auth: false,
        body: formData,
      });
    } catch (err) {
      const message = err?.message || "Unable to register.";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem("rh_refresh_token");

    try {
      await signOut(auth);
    } catch {
      // best-effort Firebase signout
    }

    try {
      if (refreshToken) {
        await apiRequest("/auth/logout", {
          method: "POST",
          auth: false,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });
      }
    } catch {
      // best-effort logout against API; local clear still proceeds
    }

    clearSession();
    setUser(null);
    setToken(null);
    setError("");
  }, []);

  const value = useMemo(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(user && token),
      loading,
      error,
      login,
      registerRenter,
      registerPersonal,
      registerBusiness,
      logout,
      clearError,
    }),
    [
      user,
      token,
      loading,
      error,
      login,
      registerRenter,
      registerPersonal,
      registerBusiness,
      logout,
      clearError,
    ],
  );

  return (
    <AuthContextWeb.Provider value={value}>{children}</AuthContextWeb.Provider>
  );
}
